import { Dropdown, Menu } from 'antd';
import {
  ActionMenuIcon,
  AmbientAiIcon,
  BinIcon,
  EditAppointmentIcon,
  NotificationsHistoryIcon,
  PatientIcon,
  QRCodeIcon,
  SendIntakeIcon,
  SummaryIcon,
  NotificationMutedIcon,
  NotificationUnMutedIcon,
} from '../../../icons';
import { useSelector } from 'react-redux';
import { CheckCircleOutlined, UndoOutlined } from '@ant-design/icons';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { useDispatch } from 'react-redux';
import {
  getAppointmentDetailsByUuidAction,
  markAsProcessedAction,
  notificationHistory,
  sendAppointmentReminders,
  markAppointmentOptOut,
  unmarkAsProcessedAction,
} from '../../../store/actions/appointment.action';
import { getFromStorage, storeInLocal } from '../../../lib/storage';
import { useNavigate } from 'react-router-dom';
import { analytics } from 'src/services';

const AppointmentActionMenu = ({
  record,
  setDeleteText,
  setIsDeleteOpen,
  setQRCodeURL,
  setIsQRCodeOpen,
  setEditAppointmentOpen,
  setStoreRecord,
  setIsnotificationHistoryOpen,
  setShowConvertAppointmentToAuraModal,
  setSelectedAppointmentForAuraEncounter,
  setFilteredData,
  setData,
}) => {
  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const { user } = useAuth();

  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const dispatch = useDispatch();
  const isAdmin = settingsOrgValue?.roles?.includes('admin');
  const { isEntitled: hasVCAProcessedWorkflowAccess } = useFeatureEntitlements(
    'vca-processed-status'
  );
  const cachedUserEmail = getFromStorage('user_email');
  const navigate = useNavigate();

  const handleRowClick = (record, type) => {
    storeInLocal('uuid', record?.appt_uuid);
    analytics.track('Opened CareConnect Summary', {
      appointment_date: record?.appointmentDateTime,
      appointment_id: record?.appointment_id,
      appointment_status: record?.intakeStatus,
      practitioner_name: record?.practitioner_Name,
      email: user?.email,
    });
    if (type === 'Menu') {
      analytics.track('Clicked View Summary Action Menu', {
        appointment_date: record?.appointmentDateTime,
        appointment_id: record?.appointment_id,
        appointment_status: record?.intakeStatus,
        practitioner_name: record?.practitioner_Name,
        email: user?.email,
      });
    }
    navigate(`/schedule/${record?.appt_uuid}`);
  };

  const handleSendReminder = (uuid, record) => {
    analytics.track('Send Intake Reminder', {
      appointment_date: record?.appointmentDateTime,
      appointment_id: record?.appointment_id,
      appointment_status: record?.intakeStatus,
      practitioner_name: record?.practitioner_Name,
      email: user?.email,
    });
    dispatch(sendAppointmentReminders(uuid, cachedUserEmail));
  };

  const handleDeleteOpen = (appt_uuid, record) => {
    analytics.track('Clicked Remove Appointment', {
      appointment_date: record?.appointmentDateTime,
      appointment_id: record?.appointment_id,
      appointment_status: record?.intakeStatus,
      practitioner_name: record?.practitioner_Name,
      email: user?.email,
    });
    setDeleteText(record);
    setIsDeleteOpen(true);
  };

  const handleShowQRCode = (url, record) => {
    analytics.track('Clicked Show QR Code', {
      appointment_date: record?.appointmentDateTime,
      appointment_id: record?.appointment_id,
      appointment_status: record?.intakeStatus,
      practitioner_name: record?.practitioner_Name,
      email: user?.email,
    });
    setQRCodeURL(url);
    setIsQRCodeOpen(true);
  };

  const handlePatientChatOpen = (url, record) => {
    if (url) {
      analytics.track('Opened Lumi Chat', {
        appointment_date: record?.appointmentDateTime,
        appointment_id: record?.appointment_id,
        appointment_status: record?.intakeStatus,
        practitioner_name: record?.practitioner_Name,
        email: user?.email,
      });
      window.open(url, '_blank'); // Open the virtual care site URL
    }
  };

  const handleEditAppointmentModal = (appt_uuid, record) => {
    analytics.track('Clicked Edit Appointment', {
      appointment_date: record?.appointmentDateTime,
      appointment_id: record?.appointment_id,
      appointment_status: record?.intakeStatus,
      practitioner_name: record?.practitioner_Name,
      email: user?.email,
    });
    dispatch(
      getAppointmentDetailsByUuidAction(appt_uuid, setEditAppointmentOpen)
    );
  };

  const handleNotificationHistoryModal = (appointmentId, record) => {
    setStoreRecord(record);
    const notificationHistory_payload = {
      appointment_id: appointmentId,
    };
    dispatch(
      notificationHistory(
        notificationHistory_payload,
        setIsnotificationHistoryOpen
      )
    );
  };

  const handleConvertAppointmentToAuraModal = (appointmentId, record) => {
    setShowConvertAppointmentToAuraModal(true);
    setSelectedAppointmentForAuraEncounter({
      appointmentId,
      record,
    });
  };

  const handleOptOut = (appt_uuid, record, isOptOut) => {
    analytics.track(
      `Clicked ${isOptOut ? 'Mute' : 'Unmute'} Patient Notifications`,
      {
        appointment_date: record?.appointmentDateTime,
        appointment_id: record?.appointment_id,
        appointment_status: record?.intakeStatus,
        practitioner_name: record?.practitioner_Name,
        email: user?.email,
      }
    );
    dispatch(
      markAppointmentOptOut(appt_uuid, isOptOut, () => {
        setFilteredData((prevData) => {
          if (!prevData) return;
          return prevData.map((item) => {
            if (item.appointment_id === record?.appointment_id) {
              return {
                ...item,
                isOptOut: isOptOut,
              };
            }
            return item;
          });
        });

        setData((prevData) => {
          if (!prevData) return;
          return prevData.map((item) => {
            if (item.appointment_id === record?.appointment_id) {
              return {
                ...item,
                isOptOut: isOptOut,
              };
            }
            return item;
          });
        });
      })
    );
  };

  const handleMarkAsProcessed = (appointment_id, record) => {
    analytics.track('Marked Appointment As Processed', {
      appointment_date: record?.appointmentDateTime,
      appointment_id: record?.appointment_id,
      appointment_status: record?.intakeStatus,
      practitioner_name: record?.practitioner_Name,
      email: user?.email,
    });
    dispatch(markAsProcessedAction(appointment_id)).then((res) => {
      if (res) {
        setFilteredData((prevData) => {
          if (!prevData) return;
          return prevData.map((item) => {
            if (item.appointment_id === appointment_id) {
              return {
                ...item,
                isProcessed: true,
              };
            }
            return item;
          });
        });

        setData((prevData) => {
          if (!prevData) return;
          return prevData.map((item) => {
            if (item.appointment_id === appointment_id) {
              return {
                ...item,
                isProcessed: true,
              };
            }
            return item;
          });
        });
      }
    });
  };

  const handleUnmarkAsProcessed = (appointment_id, record) => {
    analytics.track('Reverted Appointment To Completed', {
      appointment_date: record?.appointmentDateTime,
      appointment_id: record?.appointment_id,
      appointment_status: record?.intakeStatus,
      practitioner_name: record?.practitioner_Name,
      email: user?.email,
    });
    dispatch(unmarkAsProcessedAction(appointment_id)).then((res) => {
      if (res) {
        setFilteredData((prevData) => {
          if (!prevData) return;
          return prevData.map((item) => {
            if (item.appointment_id === appointment_id) {
              return {
                ...item,
                isProcessed: false,
              };
            }
            return item;
          });
        });

        setData((prevData) => {
          if (!prevData) return;
          return prevData.map((item) => {
            if (item.appointment_id === appointment_id) {
              return {
                ...item,
                isProcessed: false,
              };
            }
            return item;
          });
        });
      }
    });
  };

  const menuOptions = (id, record) => {
    const conditionForReminder = record?.actions;
    const canEditAppointments =
      (!record?.isExpired && record?.intakeStatus !== 'Completed') ||
      (isAdmin && (record?.intakeStatus === 'Completed' || record?.isExpired));

    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'reminder' && conditionForReminder) {
            handleSendReminder(id, record);
          }
          if (key === 'delete') {
            handleDeleteOpen(id, record);
          }
          if (key === 'patientChat') {
            handlePatientChatOpen(record?.llm_chat_url, record);
          }
          if (key === 'showQRCode') {
            handleShowQRCode(record?.llm_chat_url, record);
          }
          if (key === 'intake') {
            handleRowClick(record, 'Menu');
          }
          if (key === 'editAppointment') {
            handleEditAppointmentModal(record?.appt_uuid, record);
          }
          if (key === 'notificationHistory') {
            handleNotificationHistoryModal(record?.appointment_id, record);
          }
          if (key === 'convertToAuraEncounter') {
            handleConvertAppointmentToAuraModal(record?.appointment_id, record);
          }
          if (key === 'markAsProcessed') {
            handleMarkAsProcessed(record?.appointment_id, record);
          }
          if (key === 'unmarkAsProcessed') {
            handleUnmarkAsProcessed(record?.appointment_id, record);
          }
          if (key === 'optOut') {
            handleOptOut(record?.appt_uuid, record, true);
          }
          if (key === 'unmute') {
            handleOptOut(record?.appt_uuid, record, false);
          }
        }}
      >
        {conditionForReminder &&
          record?.isExpired === false &&
          record?.intakeStatus != 'Unavailable' &&
          record?.intakeStatus != 'Completed' && (
            <Menu.Item key="reminder">
              <span className="flex items-center dropdown-text space-x-2">
                <SendIntakeIcon /> <span>Send intake reminder</span>
              </span>
            </Menu.Item>
          )}
        <Menu.Item key="intake">
          <span className="dropdown-text flex items-center space-x-2">
            <SummaryIcon /> <span>View summary</span>
          </span>
        </Menu.Item>
        {record?.isExpired === false &&
          record?.intakeStatus != 'Unavailable' && (
            <Menu.Item key="patientChat">
              <span className="dropdown-text flex items-center space-x-1">
                <PatientIcon /> <span>Open chat as patient</span>
              </span>
            </Menu.Item>
          )}
        {record?.isExpired === false &&
          currentPractitioner?.org_settings?.show_qr_code && (
            <Menu.Item key="showQRCode">
              <span className="dropdown-text flex items-center space-x-1">
                <QRCodeIcon /> <span>Show QR Code</span>
              </span>
            </Menu.Item>
          )}

        {record?.intakeStatus === 'New' &&
          !!record.llm_chat_url &&
          !record?.isExpired && (
            <Menu.Item key="convertToAuraEncounter">
              <span className="dropdown-text flex items-center space-x-2">
                <AmbientAiIcon /> <span>Convert to Aura Encounter</span>
              </span>
            </Menu.Item>
          )}
        {canEditAppointments && (
          <Menu.Item key="editAppointment">
            <span className="dropdown-text flex items-center space-x-2">
              <EditAppointmentIcon /> <span>Edit appointment</span>
            </span>
          </Menu.Item>
        )}
        <Menu.Item key="notificationHistory">
          <span className="dropdown-text flex items-center space-x-2">
            <NotificationsHistoryIcon /> <span>Show notification history</span>
          </span>
        </Menu.Item>
        {hasVCAProcessedWorkflowAccess &&
          (record?.intakeStatus === 'Completed' ||
            record?.intakeStatus === 'partial_completed' ||
            record?.intakeStatus === 'InProgress') &&
          !record?.isProcessed && (
            <Menu.Item key="markAsProcessed">
              <span className="dropdown-text flex items-center space-x-2">
                <CheckCircleOutlined className="text-[#1c7430]" />{' '}
                <span>Mark as processed</span>
              </span>
            </Menu.Item>
          )}
        {hasVCAProcessedWorkflowAccess &&
          (record?.intakeStatus === 'Completed' ||
            record?.intakeStatus === 'partial_completed' ||
            record?.intakeStatus === 'InProgress') &&
          record?.isProcessed && (
            <Menu.Item key="unmarkAsProcessed">
              <span className="dropdown-text flex items-center space-x-2">
                <UndoOutlined className="text-gray-500" />{' '}
                <span>
                  Revert back to{' '}
                  {record?.intakeStatus === 'Completed'
                    ? 'completed'
                    : record?.intakeStatus === 'InProgress'
                      ? 'in progress'
                      : 'partial summary'}
                </span>
              </span>
            </Menu.Item>
          )}
        {record?.intakeStatus !== 'Unavailable' &&
          record?.intakeStatus !== 'Completed' &&
          record?.isOptOut === false && (
            <Menu.Item key="optOut">
              <span className="dropdown-text flex items-center space-x-2">
                <NotificationMutedIcon color="#F0000E" width="17" height="17" />{' '}
                <span>Mute patient notifications</span>
              </span>
            </Menu.Item>
          )}
        {record?.isOptOut && (
          <Menu.Item key="unmute">
            <span className="dropdown-text flex items-center space-x-2">
              <NotificationUnMutedIcon color="#16A878" width="17" height="17" />{' '}
              <span>Unmute patient notifications</span>
            </span>
          </Menu.Item>
        )}
        <Menu.Item key="delete">
          <span className="dropdown-text flex items-center space-x-2">
            <BinIcon /> <span>Remove appointment</span>
          </span>
        </Menu.Item>
      </Menu>
    );
  };
  return (
    <>
      <Dropdown
        className="actionMenu"
        overlay={menuOptions(record?.appt_uuid, record)}
      >
        <a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
          <ActionMenuIcon />
        </a>
      </Dropdown>
    </>
  );
};

export default AppointmentActionMenu;
